/**
 * Share… on an asset (0.31.0): a public link to its proxy player, or a request for someone in PRISM to approve it.
 * The link is shown once, when it is made (PRISM keeps only a hash of it).
 */
import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Copy, ExternalLink, Link2, Loader2, Share2, UserCheck } from 'lucide-react';
import { SHARE_DEFAULT_DAYS, type AssetDetail, type CreatedShareDTO } from '@prism/contracts';
import { api, errorMessage } from '../api/client';
import { useAssignees, useMe, useRightsSettings } from '../api/hooks';
import { RightsCheckBlock, useRightsCheck } from './Preflight';
import { assetRightsProblems, isBlockingProblem } from './rightsAck';
import { Modal } from './Modal';
import { toast } from './Toasts';
import { queryKeys } from '../api/queryKeys';

/** YYYY-MM-DD in local time, `days` from today. */
const dateIn = (days: number) => {
  const d = new Date(Date.now() + days * 86_400_000);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

export function ShareButton({ asset }: { asset: AssetDetail }) {
  const me = useMe().data;
  const [open, setOpen] = useState(false);
  if (!me?.permissions.includes('asset.share') || asset.deleted) return null;
  return (
    <>
      <button className="btn sm" onClick={() => setOpen(true)} data-testid="share-asset" title="Share a link, or ask someone to approve"><Share2 size={14} /> Share</button>
      {open && <ShareDialog asset={asset} onClose={() => setOpen(false)} />}
    </>
  );
}

function ShareDialog({ asset, onClose }: { asset: AssetDetail; onClose: () => void }) {
  const qc = useQueryClient();
  const me = useMe().data!;
  const people = (useAssignees().data?.people ?? []).filter((p) => p.id !== me.user.id);
  const playable = Boolean(asset.media.proxyUrl);
  const [mode, setMode] = useState<'link' | 'person'>(playable ? 'link' : 'person');
  const [expiry, setExpiry] = useState(dateIn(SHARE_DEFAULT_DAYS));
  const [never, setNever] = useState(false);
  const [download, setDownload] = useState(false);
  const [approval, setApproval] = useState(false);
  const [instructions, setInstructions] = useState('');
  const [person, setPerson] = useState('');
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState<CreatedShareDTO | null>(null);
  const url = created ? `${window.location.origin}${created.path}` : '';
  // 1.8.0: the asset's rights under the policy (the server checks again); a withdrawn asset can't be shared at all.
  const rightsSettings = useRightsSettings(Boolean(asset.rights)).data;
  const rightsProblems = assetRightsProblems(asset, rightsSettings);
  const rightsBlocked = rightsProblems.filter(isBlockingProblem);
  const rights = useRightsCheck(rightsProblems);

  async function makeLink() {
    setBusy(true);
    try {
      const r = await api<CreatedShareDTO>(`/api/v1/assets/${asset.id}/shares`, {
        method: 'POST',
        // The end of the chosen day, in local time.
        json: { expiresAt: never ? null : new Date(`${expiry}T23:59:59`).toISOString(), allowDownload: download, approval, instructions: approval ? instructions : '', ...rights.field() },
      });
      setCreated(r);
      await Promise.all([qc.invalidateQueries({ queryKey: queryKeys.shares(asset.id) }), qc.invalidateQueries({ queryKey: queryKeys.approvalsAll() })]);
    } catch (err) {
      toast(rights.takeError(err) ? 'Check the rights first' : errorMessage(err), 'error');
    } finally {
      setBusy(false);
    }
  }
  async function askPerson() {
    setBusy(true);
    try {
      await api(`/api/v1/assets/${asset.id}/approvals`, { method: 'POST', json: { approverUserId: person, instructions, ...rights.field() } });
      await qc.invalidateQueries({ queryKey: queryKeys.approvalsAll() });
      toast(`Asked ${people.find((p) => p.id === person)?.name ?? 'them'} to approve; it is in their Tasks`);
      onClose();
    } catch (err) {
      toast(rights.takeError(err) ? 'Check the rights first' : errorMessage(err), 'error');
    } finally {
      setBusy(false);
    }
  }
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast('Link copied');
    } catch {
      toast('Select the link and copy it', 'error');
    }
  };

  if (created) {
    return (
      <Modal title="Link ready" onClose={onClose} actions={<button className="btn primary" onClick={onClose}>Done</button>}>
        <p className="muted" style={{ margin: 0 }}>Anyone with this link can watch the proxy{created.allowDownload ? ' and download it' : ''}{created.approval ? ', and approve or request changes' : ''}, {created.expiresAt ? `until ${new Date(created.expiresAt).toLocaleDateString()}` : 'until you revoke it'}. Copy it now: PRISM keeps only a fingerprint of it, so it cannot show it again.</p>
        <div style={{ display: 'flex', gap: 6 }}>
          <input className="input mono" readOnly value={url} onFocus={(e) => e.currentTarget.select()} aria-label="Share link" data-testid="share-url" />
          <button className="btn" onClick={() => void copy()} data-testid="share-copy"><Copy size={14} /> Copy</button>
          <a className="btn ghost" href={url} target="_blank" rel="noreferrer noopener"><ExternalLink size={14} /></a>
        </div>
      </Modal>
    );
  }

  return (
    <Modal title={`Share “${asset.title}”`} onClose={onClose} actions={<>
      <button className="btn ghost" onClick={onClose}>Cancel</button>
      {mode === 'link'
        ? <button className="btn primary" disabled={busy || (!never && !expiry) || rightsBlocked.length > 0 || !rights.ready} onClick={() => void makeLink()} data-testid="share-create">{busy ? <Loader2 size={14} className="spin" /> : <Link2 size={14} />} Create link</button>
        : <button className="btn primary" disabled={busy || !person || rightsBlocked.length > 0 || !rights.ready} onClick={() => void askPerson()} data-testid="share-ask">{busy ? <Loader2 size={14} className="spin" /> : <UserCheck size={14} />} Ask for approval</button>}
    </>}>
      <div className="segmented" role="tablist" aria-label="How to share">
        <button aria-pressed={mode === 'link'} disabled={!playable} onClick={() => setMode('link')} data-testid="share-mode-link" title={playable ? undefined : 'There is no proxy to show yet'}><Link2 size={13} /> Public link</button>
        <button aria-pressed={mode === 'person'} onClick={() => setMode('person')} data-testid="share-mode-person"><UserCheck size={13} /> Someone in PRISM</button>
      </div>
      {rightsBlocked.length > 0 && (
        <div className="banner error" role="alert" data-testid="preflight-problems">
          <div className="banner-body"><strong>This asset can’t be shared</strong>{rightsBlocked.map((p, i) => <span key={i} data-code={p.code}>{p.message}</span>)}</div>
        </div>
      )}
      <RightsCheckBlock check={rights} />
      {mode === 'link' ? (
        <>
          <p className="muted" style={{ margin: 0 }}>The link opens
 a page with the title and the proxy player only, for anyone who has it, without signing in. The original is never shared.</p>
          <div className="field">
            <label htmlFor="share-expiry">Expires</label>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              <input id="share-expiry" className="input" type="date" style={{ maxWidth: 180 }} disabled={never} value={expiry} min={dateIn(1)} onChange={(e) => setExpiry(e.target.value)} data-testid="share-expiry" />
              <label className="toggle-row"><input type="checkbox" checked={never} onChange={(e) => setNever(e.target.checked)} data-testid="share-never" /><span>Never</span></label>
            </div>
          </div>
          <label className="toggle-row"><input type="checkbox" checked={download} onChange={(e) => setDownload(e.target.checked)} data-testid="share-download" /><span>Allow download (the proxy, never the original)</span></label>
          <label className="toggle-row"><input type="checkbox" checked={approval} onChange={(e) => setApproval(e.target.checked)} data-testid="share-approval" /><span>Ask for approval: anyone with the link can approve or request changes, and comment at a point in the video</span></label>
          {approval && <div className="field"><label htmlFor="share-ins">Instructions for reviewers</label><textarea id="share-ins" className="textarea" rows={3} maxLength={2000} value={instructions} onChange={(e) => setInstructions(e.target.value)} data-testid="share-instructions" /></div>}
        </>
      ) : (
        <>
          <p className="muted" style={{ margin: 0 }}>They get an approval in Tasks and open the player in PRISM. They need to be able to see this asset.</p>
          <div className="field"><label htmlFor="share-person">Who approves</label>
            <select id="share-person" className="select" value={person} onChange={(e) => setPerson(e.target.value)} data-testid="share-person">
              <option value="">Choose someone…</option>
              {people.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          <div className="field"><label htmlFor="share-pins">Instructions</label><textarea id="share-pins" className="textarea" rows={3} maxLength={2000} value={instructions} onChange={(e) => setInstructions(e.target.value)} data-testid="share-person-instructions" /></div>
        </>
      )}
    </Modal>
  );
}
